import { NewHighlight } from "react-pdf-highlighter";
import { filterSeparatorRects } from "@libertify.frontend/helpers";

export function filterOverlappingRects(
  rects,
  horizOverlapThreshold = 0.9,
  vertOverlapThreshold = 0.3
) {
  if (!rects || rects.length <= 1) return rects;

  // tracking array to determine which rectangles should be kept after filtering
  const keep = new Array(rects.length).fill(true);

  for (let i = 0; i < rects.length; i++) {
    // If rect was already marked for removal by a previous comparison, skip it.
    if (!keep[i]) {
      continue;
    }
    const A = rects[i];
    const widthA = A.x2 - A.x1;
    const heightA = A.y2 - A.y1;

    // Compare A with every other rectangle B
    for (let j = i + 1; j < rects.length; j++) {
      if (!keep[j]) {
        continue;
      }
      const B = rects[j];
      const widthB = B.x2 - B.x1;
      const heightB = B.y2 - B.y1;

      // Calculate overlap
      const overlapWidth = Math.min(A.x2, B.x2) - Math.max(A.x1, B.x1);
      const overlapHeight = Math.min(A.y2, B.y2) - Math.max(A.y1, B.y1);
      if (overlapWidth <= 0 || overlapHeight <= 0) {
        continue;
      }

      // Determine the shorter rect
      let smallerRect, smallerWidth, smallerHeight;
      if (widthA <= widthB) {
        smallerRect = A;
        smallerWidth = widthA;
        smallerHeight = heightA;
      } else {
        smallerRect = B;
        smallerWidth = widthB;
        smallerHeight = heightB;
      }

      const horizOverlapRatio = overlapWidth / smallerWidth;
      const vertOverlapRatio = overlapHeight / smallerHeight;

      // Check if both horizontal & vertical overlaps exceed the thresholds
      if (
        horizOverlapRatio > horizOverlapThreshold &&
        vertOverlapRatio > vertOverlapThreshold
      ) {
        // Remove the smaller rect
        if (smallerRect === A) {
          keep[i] = false;
        } else {
          keep[j] = false;
        }
        if (!keep[i]) break;
      }
    }
  }
  const filteredRects = rects.filter((rect, k) => keep[k]);
  return filteredRects;
}

export const filterTextHighlightRects = (
  newhighlight: NewHighlight
): NewHighlight => {
  // Filter out separators first
  const rectsWithoutSeparators = filterSeparatorRects(
    newhighlight.position.rects
  );

  // Then apply existing overlapping filter
  const filteredRects = filterOverlappingRects(
    rectsWithoutSeparators,
    0.9,
    0.3
  );

  const updatedBoundingRect = {
    x1: Math.min(...filteredRects.map((r) => r.x1)),
    y1: Math.min(...filteredRects.map((r) => r.y1)),
    x2: Math.max(...filteredRects.map((r) => r.x2)),
    y2: Math.max(...filteredRects.map((r) => r.y2)),
    width: newhighlight.position.boundingRect.width,
    height: newhighlight.position.boundingRect.height,
    pageNumber: newhighlight.position.boundingRect.pageNumber,
  };

  return {
    ...newhighlight,
    position: {
      ...newhighlight.position,
      rects: filteredRects,
      boundingRect: updatedBoundingRect,
    },
  };
};
